import { useEffect, useMemo } from 'react';
import type { ProfileState } from '@/hooks/useUser';
import { useWatchlistMutations } from '@/pages/Watchlist/hooks/useWatchlistMutations';
import { useWatchlist, useWatchlists } from '@/hooks/useWatchlist';
import { doesCompanyExistInWatchlist } from '@/pages/Watchlist/utils';

interface Props {
  user: ProfileState;
  companyId: string;
}

export function AutoAddCompanyToWatchlist({ user, companyId }: Props) {
  const { isWatchlistBannerDismissed } = user;
  const { data: watchlist } = useWatchlist();
  const { addCompany } = useWatchlistMutations();
  const { data: watchlists, status: watchlistsStatus } = useWatchlists();
  const watchlistsKeys = useMemo(
    () => watchlists?.flatMap((w) => w.items.map((n) => n.companyId)) ?? [],
    [watchlists]
  );

  useEffect(() => {
    if (isWatchlistBannerDismissed) {
      return;
    }

    // Watchlist or company unavailable
    if (!watchlist?.id || typeof companyId === 'undefined') {
      return;
    }

    // Watchlists unavailable or already has 3+ companies
    if (watchlistsStatus !== 'success' || watchlistsKeys.length >= 3) {
      return;
    }

    // Company already added to watchlist
    if (doesCompanyExistInWatchlist(companyId, watchlistsKeys)) {
      return;
    }

    addCompany({
      id: companyId,
      includeSnackBar: true,
      watchlistId: watchlist?.id,
      autoAdded: true,
    });
  }, [
    addCompany,
    companyId,
    isWatchlistBannerDismissed,
    watchlist,
    watchlistsKeys,
    watchlistsStatus,
  ]);

  return null;
}
